import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Cog, Cpu, Zap, Headphones, Wrench, Truck, Award } from "lucide-react";
import { PRODUCTS, PROJECTS } from "../data/products";
import useCountUp from "../hooks/useCountUp";
import PicImg from "./PicImg";

// Small animated counter component
function StatCounter({ value, label, suffix = "", className = "" }) {
  const { ref, display } = useCountUp(value, { duration: 1800, suffix });
  return (
    <div ref={ref} className={`flex flex-col ${className}`}>
      <span className="font-display text-4xl md:text-5xl text-liftile-orange leading-none">{display}</span>
      <span className="font-heading text-[10px] md:text-xs uppercase tracking-[0.2em] text-liftile-cream/60 mt-2">{label}</span>
    </div>
  );
}

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

// 1. Product Categories — "Our Products" grid
export function ProductCategoriesSection() {
  // Show the 6 hero products from data
  const featured = [
    "conventional-tower-hoist",
    "multi-functional-tower-hoist",
    "passenger-cum-material-hoist",
    "concrete-mixer",
    "reversible-drum-concrete-mixer",
    "bar-bending-machine",
  ]
    .map((slug) => PRODUCTS.find((p) => p.slug === slug))
    .filter(Boolean);

  return (
    <section data-testid="product-categories" className="bg-white py-24 md:py-32 text-liftile-matte-black">
      <div className="container-x">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-14 md:mb-16 max-w-3xl mx-auto">
          <div className="heading-eyebrow mb-4">Our Catalog</div>
          <h2 className="display-2 text-liftile-matte-black">
            Our <span className="text-liftile-orange">Products</span>
          </h2>
          <p className="font-body text-liftile-mid-gray text-base md:text-lg mt-5">
            Reliable Construction Equipment Built for Performance, Safety &amp; Durability.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {featured.map((p, i) => (
            <motion.div
              key={p.slug}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ delay: i * 0.07 }}
            >
              <Link
                to={`/products/${p.slug}`}
                data-testid={`product-card-${p.slug}`}
                className="group block bg-white rounded-2xl overflow-hidden border-2 border-transparent shadow-[0_8px_30px_-10px_rgba(17,17,17,0.12)] hover:border-liftile-orange hover:shadow-[0_25px_60px_-20px_rgba(255,107,0,0.45)] hover:-translate-y-1 transition-all duration-500 h-full flex flex-col"
              >
                <div className="aspect-square bg-liftile-cream-light/60 p-6 md:p-8 flex items-center justify-center overflow-hidden">
                  <PicImg
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="max-w-full max-h-full object-contain transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="p-6 md:p-7 flex flex-col flex-1">
                  <div className="font-heading text-[10px] uppercase tracking-[0.25em] text-liftile-orange">{p.category}</div>
                  <h3 className="font-heading text-lg md:text-xl uppercase tracking-wide font-bold text-liftile-matte-black mt-2 leading-tight">{p.name}</h3>
                  <p className="text-sm text-liftile-mid-gray/75 mt-3 leading-relaxed">{p.short_description}</p>
                  <div className="mt-auto pt-6 flex items-center justify-between">
                    <span className="inline-flex items-center gap-2 font-heading text-xs md:text-sm uppercase tracking-wider font-semibold text-liftile-orange transition-all group-hover:gap-3">
                      View Details
                      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                    <span className="w-10 h-10 grid place-items-center rounded-full bg-liftile-matte-black text-white transition-all duration-500 group-hover:bg-liftile-orange group-hover:shadow-[0_0_25px_rgba(255,107,0,0.6)]">
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="flex justify-center mt-14">
          <Link
            to="/products"
            data-testid="view-all-products"
            className="group inline-flex items-center gap-3 border-2 border-liftile-matte-black text-liftile-matte-black font-heading text-xs md:text-sm uppercase tracking-wider font-semibold px-7 py-4 rounded-sm hover:bg-liftile-orange hover:border-liftile-orange hover:text-white hover:shadow-[0_15px_40px_-10px_rgba(255,107,0,0.6)] transition-all"
          >
            View All Products
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

// 2. Why Choose Liftile
export function WhyChooseSection() {
  const features = [
    { Icon: ShieldCheck, title: "Safety First", desc: "Engineered to meet global safety standards." },
    { Icon: Award, title: "Premium Quality", desc: "Built with high-grade materials for long life." },
    { Icon: Cpu, title: "Advanced Technology", desc: "Innovative design for maximum efficiency." },
    { Icon: Zap, title: "High Performance", desc: "Powerful equipment for better productivity." },
    { Icon: Headphones, title: "Dedicated Support", desc: "Expert support team always ready to assist." },
  ];

  return (
    <section data-testid="why-choose" className="bg-liftile-cream-light py-20 md:py-28 text-liftile-matte-black">
      <div className="container-x">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-14">
          <div className="heading-eyebrow mb-3">Why Liftile</div>
          <h2 className="display-2 text-liftile-matte-black">
            Why Choose <span className="text-liftile-orange">LIFTILE?</span>
          </h2>
          <p className="font-body text-liftile-mid-gray/80 mt-4 max-w-2xl mx-auto">
            We are committed to delivering equipment that ensures safety, improves productivity, and builds a stronger future.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ delay: i * 0.08 }}
              className="bg-white p-7 border border-liftile-mid-gray/10 hover:border-liftile-orange hover:-translate-y-1 transition-all"
            >
              <div className="w-14 h-14 grid place-items-center border-2 border-liftile-orange text-liftile-orange mb-5">
                <f.Icon className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-base uppercase tracking-wider font-semibold mb-2">{f.title}</h3>
              <p className="text-sm text-liftile-mid-gray/80 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// 3. Applications / Solutions
export function ApplicationsSection() {
  const apps = [
    { title: "High-Rise Construction", img: "/solution-highrise.jpg" },
    { title: "Infrastructure Projects", img: "/solution-infrastructure.jpg" },
    { title: "Commercial Buildings", img: "/solution-commercial.jpg" },
    { title: "Residential Projects", img: "/solution-residential.jpg" },
    { title: "Industrial Projects", img: "/solution-industrial.jpg" },
  ];

  return (
    <section data-testid="applications" className="bg-liftile-black py-24 md:py-32">
      <div className="container-x">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-14 md:mb-16">
          <div className="heading-eyebrow mb-4">Complete Solutions</div>
          <h2 className="display-2 text-liftile-cream max-w-3xl">
            For Every <span className="text-liftile-orange">Construction</span> Need
          </h2>
          <p className="text-liftile-cream/60 mt-5 max-w-xl font-body text-base md:text-lg">
            From material handling to concrete preparation, LIFTILE provides end-to-end solutions for modern construction.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
          {apps.map((a, i) => (
            <motion.div
              key={a.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ delay: i * 0.05 }}
              className="group relative aspect-[3/4] overflow-hidden border border-white/10 hover:border-liftile-orange transition-all duration-500 hover:-translate-y-1"
            >
              <PicImg src={a.img} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" decoding="async" />
              <div className="absolute inset-0 bg-gradient-to-t from-liftile-black via-liftile-black/40 to-transparent transition-opacity duration-500 group-hover:from-liftile-black/95" />
              {/* Orange shimmer on hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent 60%, rgba(255,107,26,0.12) 100%)" }} />
              <div className="absolute bottom-5 left-5 right-5">
                <h3 className="font-heading text-sm md:text-base uppercase tracking-wider text-liftile-cream leading-tight">{a.title}</h3>
                <div className="w-8 h-0.5 bg-liftile-orange mt-3 group-hover:w-14 transition-all duration-500" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// 4. Dealer CTA
export function DealerCTASection() {
  return (
    <section data-testid="dealer-cta" className="relative bg-liftile-matte-black py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 industrial-grid opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-r from-liftile-black via-transparent to-liftile-orange/10" />

      <div className="container-x relative grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <div className="heading-eyebrow mb-3">Become A Dealer</div>
          <h2 className="display-2 text-liftile-cream">
            Partner With <span className="text-liftile-orange">India&apos;s Fastest Growing</span> Lifting Brand
          </h2>
          <p className="text-liftile-cream/70 mt-5 max-w-xl font-body">
            Join our growing network of authorised dealers across India. Premium products, attractive margins, dedicated tech support — and a brand customers trust.
          </p>

          <div className="grid grid-cols-2 gap-4 mt-8">
            <div className="border-l-2 border-liftile-orange pl-4">
              <StatCounter value={20} label="Active Dealers" suffix="+" />
            </div>
            <div className="border-l-2 border-liftile-orange pl-4">
              <div className="font-display text-4xl md:text-5xl text-liftile-orange leading-none">15–30%</div>
              <div className="font-heading text-[10px] md:text-xs uppercase tracking-[0.2em] text-liftile-cream/60 mt-2">Dealer Margin</div>
            </div>
            <div className="border-l-2 border-liftile-orange pl-4">
              <div className="font-display text-4xl md:text-5xl text-liftile-orange leading-none">24/7</div>
              <div className="font-heading text-[10px] md:text-xs uppercase tracking-[0.2em] text-liftile-cream/60 mt-2">Strong Support</div>
            </div>
            <div className="border-l-2 border-liftile-orange pl-4">
              <div className="font-display text-4xl md:text-5xl text-liftile-orange leading-none">Pan India</div>
              <div className="font-heading text-[10px] md:text-xs uppercase tracking-[0.2em] text-liftile-cream/60 mt-2">Opportunities</div>
            </div>
          </div>

          <Link to="/dealer" data-testid="dealer-cta-btn" className="btn-primary mt-9 group">
            Apply For Dealership
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="relative h-[400px] md:h-[500px]">
          <div className="absolute inset-0 overflow-hidden">
            <PicImg
              src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1400&q=85"
              alt="Dealer partnership handshake at construction site"
              className="w-full h-full object-cover" loading="lazy" decoding="async" />
            <div className="absolute inset-0 bg-gradient-to-t from-liftile-black/80 to-transparent" />
          </div>
          <div className="absolute bottom-6 left-6 right-6 glass-dark border border-liftile-orange/40 p-5">
            <div className="font-heading text-sm text-liftile-orange uppercase tracking-wider">Pan India Network</div>
            <div className="text-liftile-cream mt-1">Strong dealer presence across all major states.</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// 5. Project Showcase
export function ProjectShowcase() {
  return (
    <section data-testid="projects-section" className="bg-liftile-black py-24 md:py-32">
      <div className="container-x">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14 md:mb-16">
          <div>
            <div className="heading-eyebrow mb-3">Our Projects</div>
            <h2 className="display-2 text-liftile-cream">
              Our Projects. <span className="text-liftile-orange">Our Pride.</span>
            </h2>
          </div>
          <div className="flex gap-10 md:gap-14">
            <StatCounter value={600} suffix="+" label="Happy Customers" />
            <StatCounter value={600} suffix="+" label="Projects Completed" />
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PROJECTS.slice(0, 6).map((p, i) => (
            <motion.div
              key={p.location}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ delay: i * 0.05 }}
              data-testid={`project-card-${i}`}
              className="group relative aspect-[4/3] overflow-hidden"
            >
              <PicImg
                src={p.image}
                alt={p.location}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1200ms] ease-out"
              />
              {/* Dark cinematic gradient — heavier at the bottom for label legibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-liftile-black via-liftile-black/55 to-transparent" />
              {/* Bottom-left location label */}
              <div className="absolute bottom-5 left-5 right-5">
                <div className="w-10 h-0.5 bg-liftile-orange mb-3 transition-all duration-500 group-hover:w-16" />
                <div
                  data-testid={`project-label-${i}`}
                  className="font-heading text-sm md:text-base uppercase tracking-wider text-liftile-cream leading-tight"
                >
                  {p.location}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Testimonials/Client Voices section removed per spec.
